import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, LockKeyhole, ShieldCheck, UsersRound } from "lucide-react";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";

export interface ProjectShareSettings {
  isPrivate: boolean;
  /** Existing backend contract. */
  allowedRoles: string[] | null;
}

type ShareMode = "private" | "workspace";

function shareModeFromSettings(settings: ProjectShareSettings): ShareMode {
  if (settings.isPrivate) return "private";
  return "workspace";
}

interface ProjectShareSectionProps {
  /** Initial settings seeded from the project (edit) or defaults (create). */
  initialSettings: ProjectShareSettings;
  /** The current user's role, used for the role-restriction checkbox. */
  userRole?: string;
  /** A key that, when changed, resets internal state from initialSettings. */
  resetKey: string;
  /** Called whenever the share settings change. */
  onSettingsChange: (settings: ProjectShareSettings) => void;
}

/**
 * A collapsible section containing project sharing/access controls.
 * Used inside both the project create and edit dialogs.
 */
export function ProjectShareSection({
  initialSettings,
  userRole,
  resetKey,
  onSettingsChange,
}: ProjectShareSectionProps) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<ShareMode>(() => shareModeFromSettings(initialSettings));
  const [selectedRoles, setSelectedRoles] = useState<string[]>(() => initialSettings.allowedRoles ?? []);

  // Reset internal state whenever the dialog opens (resetKey changes).
  useEffect(() => {
    setMode(shareModeFromSettings(initialSettings));
    setSelectedRoles(initialSettings.allowedRoles ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  // Propagate changes upward whenever mode or selectedRoles change.
  useEffect(() => {
    onSettingsChange({
      isPrivate: mode === "private",
      allowedRoles: mode === "workspace" && selectedRoles.length > 0 ? selectedRoles : null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, selectedRoles]);

  return (
    <Collapsible>
      <CollapsibleTrigger className="rounded-lg border px-3 py-2 text-sm font-medium">
        {t("projects:sharingAndAccess")}
        <ChevronDown className="chevron ml-1 inline size-4 transition-transform duration-200" />
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-3">
        <div className="space-y-3">
          <p className="rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-xs text-muted-foreground">
            <ShieldCheck className="mr-1.5 inline size-3.5 text-primary" />
            {t("projects:shareInternalOnly")}
          </p>

          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">{t("projects:shareAccess")}</legend>
            <label
              className={cn(
                "flex cursor-pointer gap-3 rounded-md border p-3 transition-colors",
                mode === "private" && "border-primary bg-primary/5",
              )}
            >
              <input
                type="radio"
                name="project-share-access"
                checked={mode === "private"}
                onChange={() => {
                  setMode("private");
                  setSelectedRoles([]);
                }}
                className="mt-0.5 size-4 accent-primary"
              />
              <LockKeyhole className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <span>
                <span className="block text-sm font-medium">{t("projects:sharePrivate")}</span>
                <span className="block text-xs text-muted-foreground">{t("projects:sharePrivateDescription")}</span>
              </span>
            </label>

            <label
              className={cn(
                "flex cursor-pointer gap-3 rounded-md border p-3 transition-colors",
                mode === "workspace" && "border-primary bg-primary/5",
              )}
            >
              <input
                type="radio"
                name="project-share-access"
                checked={mode === "workspace"}
                onChange={() => {
                  setMode("workspace");
                  if (userRole && userRole !== "member" && !selectedRoles.includes(userRole)) {
                    setSelectedRoles([userRole]);
                  }
                }}
                className="mt-0.5 size-4 accent-primary"
              />
              <UsersRound className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <span>
                <span className="block text-sm font-medium">{t("projects:shareWorkspace")}</span>
                <span className="block text-xs text-muted-foreground">{t("projects:shareWorkspaceDescription")}</span>
              </span>
            </label>
          </fieldset>

          {mode === "workspace" && userRole && userRole !== "member" && (
            <div className="space-y-2">
              <p className="text-sm font-medium">{t("projects:shareRestrictRoles")}</p>
              <p className="text-xs text-muted-foreground">{t("projects:shareRestrictRolesHint")}</p>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={selectedRoles.includes(userRole)}
                  onChange={(event) => {
                    setSelectedRoles((current) => (
                      event.target.checked
                        ? current.includes(userRole) ? current : [...current, userRole]
                        : current.filter((role) => role !== userRole)
                    ));
                  }}
                  className="size-4 accent-primary"
                />
                {userRole}
              </label>
            </div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
